import { useLayoutEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Home from "../components/Home";
import Login from "../components/Login";
import Register from "../components/Register";
import ServiceDetail from "../components/ServiceDetail";
import LegalPage from "../components/LegalPage";
import WhatsAppWidgetButton from "../components/WhatsAppWidgetButton";
import { normalizePath } from "./pathUtils";

const ALWAYS_SCROLL_PATHS = new Set([
  "/forgot-password",
  "/service-detail",
  "/privacy-policy",
  "/terms-and-conditions",
  "/refund-policy",
  "/security",
]);

const MOBILE_SCROLL_PATHS = new Set(["/login", "/register"]);

const MOBILE_SCROLL_QUERY = "(max-width: 767px)";

const AUTH_SCROLL_CSS = `
html.auth-scroll,
html.auth-scroll body,
html.auth-scroll #root {
  height: auto !important;
  max-height: none !important;
  min-height: 100% !important;
  overflow: visible !important;
  position: static !important;
}
html.auth-scroll {
  overflow-y: scroll !important;
  -webkit-overflow-scrolling: touch;
}
html.auth-scroll .login-app,
html.auth-scroll .App.login-app,
html.auth-scroll .login-shell,
html.auth-scroll .register-shell,
html.auth-scroll .service-detail-shell,
html.auth-scroll .sd-page,
html.auth-scroll .legal-page,
html.auth-scroll .login-form-panel,
html.auth-scroll .register-form-panel {
  display: block !important;
  position: static !important;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  min-height: 0 !important;
}
html.auth-scroll .login-shell,
html.auth-scroll .register-shell {
  display: flex !important;
  flex-direction: column !important;
  min-height: 100dvh !important;
}
html.auth-scroll .login-hero-panel {
  overflow: hidden !important;
  height: clamp(8.5rem, 26dvh, 11.5rem) !important;
  max-height: none !important;
}
@media (min-width: 768px) {
  html.auth-scroll .login-shell {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
  }
  html.auth-scroll .register-shell {
    display: flex !important;
    flex-direction: row !important;
  }
  html.auth-scroll .login-hero-panel {
    height: 100% !important;
    max-height: 100dvh !important;
  }
}
`;

function useMobileViewport() {
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia(MOBILE_SCROLL_QUERY).matches;
  });

  useLayoutEffect(() => {
    const media = window.matchMedia(MOBILE_SCROLL_QUERY);
    const sync = () => setIsMobileView(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  return isMobileView;
}

function enableAuthDocumentScroll() {
  document.documentElement.classList.add("auth-scroll");
  document.body.classList.add("auth-scroll");

  let tag = document.getElementById("auth-scroll-style");
  if (!tag) {
    tag = document.createElement("style");
    tag.id = "auth-scroll-style";
    tag.textContent = AUTH_SCROLL_CSS;
    document.head.appendChild(tag);
  }

  return () => {
    document.documentElement.classList.remove("auth-scroll");
    document.body.classList.remove("auth-scroll");
    tag.remove();
  };
}

/** Login, register, home, and other pages that do not require a session. */
export default function PublicAuthApp() {
  const location = useLocation();
  const path = normalizePath(location.pathname);
  const isHome = path === "/";
  const isMobileView = useMobileViewport();
  const isAuthScroll =
    ALWAYS_SCROLL_PATHS.has(path) ||
    (MOBILE_SCROLL_PATHS.has(path) && isMobileView);

  useLayoutEffect(() => {
    if (!isAuthScroll) return undefined;
    return enableAuthDocumentScroll();
  }, [isAuthScroll]);

  return (
    <div className={isHome ? "App home-app" : "App login-app"}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
        <Route path="/service-detail" element={<ServiceDetail />} />
        <Route path="/privacy-policy" element={<LegalPage />} />
        <Route path="/terms-and-conditions" element={<LegalPage />} />
        <Route path="/refund-policy" element={<LegalPage />} />
        <Route path="/security" element={<LegalPage />} />
        <Route path="/whatsapp-widget-button" element={<WhatsAppWidgetButton />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
